import { Button, Card, Col, Form, Input, Radio, Row, Typography, message } from "antd";
import React, { useState } from "react";
import { ArrowRightOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import AvailabilityIcon from "assets/availability.svg";
import { PartialJobInterface, JOB_STATUS } from "api/jobs/dto";
import { JobTypeValues } from "enum/job-type";
import { numberValidator } from "utils/number-validator";
import { usePostJobs } from "api/jobs/usePostJob";
import { useUpdateJob } from "api/jobs/useUpdateJob";
import { storage } from "utils/storage";
import { confirmDayjs } from "utils/date";

const { Title } = Typography;
const { TextArea } = Input;

type Props = {
  next: () => void;
  formData: PartialJobInterface;
  setFormData: React.Dispatch<React.SetStateAction<PartialJobInterface>>;
  isEditMode: boolean;
  id?: string;
};

const JobInformationForm: React.FC<Props> = ({ next, formData, setFormData, isEditMode, id }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const postJobMutation = usePostJobs();
  const updateJobMutation = useUpdateJob();
  const user = storage.getUserFromStorage();

  const handleChange = (changedValues: PartialJobInterface) => {
    setFormData({ ...formData, ...changedValues });
  };

  const handleSubmit = async () => {
    await form.validateFields();
    next();
  };

  const handleDraft = () => {
    if (loading) return;
    setLoading(true);

    const payload = {
      ...formData,
      salary: {
        amount: formData.salary?.amount ?? 0,
        period: formData.salary?.period ?? "",
        isPubliclyAvailable: formData.salary?.isPubliclyAvailable ?? true,
        isNegotiated: formData.salary?.isNegotiated ?? false,
        currency: formData.salary?.currency ?? "NPR",
      },
      status: JOB_STATUS.DRAFT,
      recruiter: user.uid,
      deadlineAt: confirmDayjs(formData.deadlineAt),
    };

    if (isEditMode && id) {
      updateJobMutation.mutate(
        { id, job: payload },
        {
          onSuccess: () => {
            message.success("Draft updated successfully!");
            setLoading(false);
          },
          onError: () => {
            message.error("Failed to update draft!");
            setLoading(false);
          },
        }
      );
    } else {
      postJobMutation.mutate(payload, {
        onSuccess: (jobRef: any) => {
          message.success("Draft saved successfully!");
          setLoading(false);
          navigate(`/recruiter/update-job/${jobRef.id}`);
        },
        onError: () => {
          message.error("Failed to save draft!");
          setLoading(false);
        },
      });
    }
  };

  React.useEffect(() => {
    form.setFieldsValue({
      jobTitle: formData?.jobTitle || "",
      companyName: formData?.companyName || "",
      experience: formData?.experience || "",
      jobDescription: formData?.jobDescription || "",
      jobType: formData?.jobType || undefined,
    });
  }, [formData, form]);

  return (
    <>
      <Card className="px-8 py-2 mt-12">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center">
          <Title level={2} style={{ fontWeight: 700 }}>
            Add Job Information
          </Title>
          <img alt="#" src={AvailabilityIcon} />
        </div>
      </Card>
      <Card className="mt-6">
        <Form layout="vertical" form={form} onFinish={handleSubmit} onValuesChange={handleChange}>
          <Row gutter={[48, 32]}>
            <Col span={24}>
              <Form.Item name="jobTitle" label="Job Title" rules={[{ required: true, message: "This is required" }]}>
                <Input placeholder="eg. UI/UX Designer" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="companyName" label="Company Name" rules={[{ required: true, message: "This is required" }]}>
                <Input placeholder="eg. Sajilo Ventures" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="experience"
                label="Experience"
                rules={[
                  { required: true, message: "This is required" },
                  { validator: numberValidator },
                ]}
              >
                <Input placeholder="eg. 2 years min" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="jobDescription" label="Job Description" rules={[{ required: true, message: "This is required" }]}>
                <TextArea className="description" rows={5} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="jobType" label="Job Type" rules={[{ required: true, message: "Please pick an item!" }]}>
                <Radio.Group optionType="button" buttonStyle="solid">
                  <Radio.Button value={JobTypeValues.FULL_TIME}>Full Time</Radio.Button>
                  <Radio.Button value={JobTypeValues.PART_TIME}>Part Time</Radio.Button>
                  <Radio.Button value={JobTypeValues.CONTRACT}>Contract</Radio.Button>
                  <Radio.Button value={JobTypeValues.INTERNSHIP}>Internship</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
          <Row justify="end">
            <div className="flex gap-x-5">
              <Button
                variant="dashed"
                icon={<InfoCircleOutlined />}
                disabled={loading}
                onClick={handleDraft}
                className="h-12 w-36"
              >
                {loading ? "Saving..." : "Save Draft"}
              </Button>
              <Button
                className="h-12 px-8 border-2 border-border text-base flex items-center gap-2 bg-primary text-white border-none"
                htmlType="submit"
                type="primary"
              >
                Continue
                <ArrowRightOutlined />
              </Button>
            </div>
          </Row>
        </Form>
      </Card>
    </>
  );
};

export default JobInformationForm;
